import React, { useState } from 'react';
import {
  AppBar, Box, CssBaseline, Drawer, IconButton, List, ListItem,
  ListItemButton, ListItemIcon, ListItemText, Toolbar, Typography,
  Menu, MenuItem, Divider, Collapse, useMediaQuery, useTheme, Button,
} from '@mui/material';
import {
  Dashboard as DashboardIcon, Map as MapIcon,
  DirectionsCar as VehiclesIcon, Assignment as RequestIcon,
  CarRental as FleetIcon, ExpandLess, ExpandMore, AccountCircle,
  Logout, Timeline, People as PeopleIcon, Shield as ShieldIcon,
  Menu as MenuIcon, Settings as AdminIcon, Visibility as VisibilityIcon,
  ReceiptLong as InvoiceIcon,
  ExitToApp as ExitIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useThemeContext } from '../contexts/ThemeContext';
import NotificationBell from '../components/NotificationBell';
import ThemeSettingsButton from '../components/ThemeSettingsButton';


import BillingGraceBanner from '../components/BillingGraceBanner';
import BillingLockScreen  from '../components/BillingLockScreen';

import { MenuItem as MenuItemType, SubMenuItem } from '../types';

const drawerWidth = 240;

const menuItems: MenuItemType[] = [
  {
    text: 'Dashboard & Analytics',
    icon: <DashboardIcon />,
    path: '/dashboard',
    hasSubItems: true,
    subItems: [
      { text: 'Dashboard', path: '/dashboard' },
      { text: 'Analytics', path: '/analytics' },
    ],
  },
  { text: 'Map', icon: <MapIcon />, path: '/map', hasSubItems: false },
  {
    text: 'Trip Playback', icon: <Timeline />, path: '/trip-playback',
    hasSubItems: false,
    requiredRoles: ['company_admin', 'admin', 'fleet_manager'],
  },
  { text: 'Vehicles & Assets', icon: <VehiclesIcon />, path: '/devices', hasSubItems: false },
  { text: 'Fleet Management', icon: <FleetIcon />, path: '/fleet-management', hasSubItems: false },
  { text: 'Vehicle Requests', icon: <RequestIcon />, path: '/vehicle-requests', hasSubItems: false },
  { text: 'Drivers', icon: <PeopleIcon />, path: '/drivers', hasSubItems: false },
  { text: 'Invoices', icon: <InvoiceIcon />, path: '/invoices', hasSubItems: false },
  {
    text: 'Geofencing', icon: <ShieldIcon />, path: '/geofencing',
    hasSubItems: false,
    requiredRoles: ['company_admin', 'admin', 'fleet_manager'],
  },
  {
    text: 'Administration', icon: <AdminIcon />, path: '/admin',
    hasSubItems: true,
    requiredRoles: ['company_admin', 'fleet_manager'],
    subItems: [
      { text: 'Users', path: '/admin/users', roles: ['company_admin', 'fleet_manager'] },
      { text: 'Company Settings', path: '/admin/company', roles: ['company_admin'] },
    ],
  },
];

interface CompanyLayoutProps { children: React.ReactNode; }

const CompanyLayout: React.FC<CompanyLayoutProps> = ({ children }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [dashboardOpen, setDashboardOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [logoKey, setLogoKey] = useState(0);

  const { user, company, logout, isRole, isImpersonating, stopImpersonating } = useAuth();
  const { mode } = useThemeContext();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleDrawerToggle = () => {
    setMobileOpen(prev => !prev);
    setLogoKey(prev => prev + 1);
  };

  const getLogoPath = () => {
    if (company?.logo) {
      if (company.logo.startsWith('http://') || company.logo.startsWith('https://')) {
        return company.logo;
      }
      const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost').replace('/api', '');
      return `${baseUrl}${company.logo}`;
    }
    return mode === 'dark' ? '/Scetru-Logo-white.png' : '/Scetru-Logo-black.png';
  };

  const isMap = location.pathname === '/map';

  const drawer = (
    <div>
      {/* Logo / company header */}
      <Box sx={{
        p: isMobile ? 1 : 1.5,
        borderBottom: '1px solid',
        borderColor: mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
        bgcolor: mode === 'dark' ? '#1e1e1e' : '#ffffff',
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        minHeight: isMobile ? '60px' : '75px',
      }}>
        <img
          key={`logo-${logoKey}`}
          src={getLogoPath()}
          alt={company?.name || 'Scetru'}
          style={{
            height: isMobile ? '45px' : '50px', width: 'auto',
            maxWidth: '95%', objectFit: 'contain',
            filter: company?.logo && mode === 'dark' ? 'brightness(1.2) contrast(0.9)' : 'none',
          }}
          onError={(e) => {
            (e.target as HTMLImageElement).src =
              mode === 'dark' ? '/Scetru-Logo-white.png' : '/Scetru-Logo-black.png';
          }}
        />
        <Typography variant="caption" sx={{
          fontWeight: 600, textAlign: 'center',
          color: mode === 'dark' ? '#b0b0b0' : 'rgba(0,0,0,0.6)',
          fontSize: isMobile ? '0.7rem' : '0.75rem',
        }}>
          {company?.name || 'Fleet Management'}
        </Typography>
      </Box>

      <List sx={{ pt: 1 }}>
        {menuItems
          .filter(item => !item.requiredRoles || item.requiredRoles.some(r => isRole(r)))
          .map((item) => (
            <React.Fragment key={item.text}>
              <ListItem disablePadding>
                <ListItemButton
                  selected={
                    location.pathname === item.path ||
                    (item.hasSubItems && item.subItems?.some(s => location.pathname === s.path))
                  }
                  onClick={() => {
                    if (item.hasSubItems && item.text === 'Dashboard & Analytics') {
                      setDashboardOpen(o => !o);
                    } else if (!item.hasSubItems) {
                      navigate(item.path);
                      if (isMobile) setMobileOpen(false);
                    }
                  }}
                  sx={{
                    color: mode === 'dark' ? '#fff' : '#000',
                    '& .MuiListItemIcon-root': { color: mode === 'dark' ? '#fff' : 'rgba(0,0,0,0.7)' },
                    '&.Mui-selected': {
                      backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.08)' : '#e6f2ff',
                      borderRight: '3px solid', borderRightColor: 'primary.main',
                      '& .MuiListItemIcon-root': { color: 'primary.main' },
                      '& .MuiListItemText-primary': { color: 'primary.main', fontWeight: 600 },
                    },
                    '&:hover': { backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)' },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} primaryTypographyProps={{ fontSize: '0.875rem' }} />
                  {item.hasSubItems && item.text === 'Dashboard & Analytics'
                    ? dashboardOpen ? <ExpandLess /> : <ExpandMore />
                    : null}
                </ListItemButton>
              </ListItem>

              {/* Dashboard sub-items */}
              {item.hasSubItems && item.text === 'Dashboard & Analytics' && (
                <Collapse in={dashboardOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {item.subItems?.map(sub => (
                      <ListItemButton key={sub.text} sx={{ pl: 4 }}
                        selected={location.pathname === sub.path}
                        onClick={() => { navigate(sub.path); if (isMobile) setMobileOpen(false); }}
                      >
                        <ListItemText primary={sub.text} primaryTypographyProps={{ fontSize: '0.8rem' }} />
                      </ListItemButton>
                    ))}
                  </List>
                </Collapse>
              )}

              {/* Administration sub-items */}
              {item.hasSubItems && item.text === 'Administration' && (
                <Collapse in timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {item.subItems
                      ?.filter(sub => !sub.roles || sub.roles.some(r => isRole(r)))
                      .map(sub => (
                        <ListItemButton key={sub.text} sx={{ pl: 4 }}
                          selected={location.pathname === sub.path}
                          onClick={() => { navigate(sub.path); if (isMobile) setMobileOpen(false); }}
                        >
                          <ListItemText primary={sub.text} primaryTypographyProps={{ fontSize: '0.8rem' }} />
                        </ListItemButton>
                      ))}
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          ))}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <CssBaseline />

      {/* ── Impersonation Banner ─────────────────────────────────────────────── */}
      {isImpersonating && (
        <Box sx={{
          position: 'fixed', top: 0, left: 0, right: 0,
          zIndex: (t) => t.zIndex.drawer + 2,
          background: 'linear-gradient(90deg, #f59e0b, #fbbf24)',
          px: 2, py: 0.75,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <VisibilityIcon sx={{ fontSize: 16, color: '#000' }} />
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, color: '#000' }}>
              Viewing as {user?.firstName} {user?.lastName}
              <Typography component="span" sx={{ fontSize: '0.72rem', fontWeight: 600, ml: 0.75, color: 'rgba(0,0,0,0.6)' }}>
                ({company?.name})
              </Typography>
            </Typography>
          </Box>
          <Button
            size="small"
            startIcon={<ExitIcon fontSize="small" />}
            onClick={stopImpersonating}
            sx={{
              fontWeight: 800, fontSize: '0.72rem', textTransform: 'none',
              background: 'rgba(0,0,0,0.12)', color: '#000', borderRadius: 1.5,
              px: 1.5, py: 0.25,
              '&:hover': { background: 'rgba(0,0,0,0.2)' },
            }}
          >
            Exit Impersonation
          </Button>
        </Box>
      )}

      <Box sx={{ display: 'flex', mt: isImpersonating ? '36px' : 0 }}>
        <AppBar position="fixed" sx={{
          top: isImpersonating ? '36px' : 0,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          backgroundColor: 'primary.main',
          zIndex: (t) => t.zIndex.drawer + 1,
          '& .MuiIconButton-root': { color: '#fff !important' },
          '& .MuiTypography-root': { color: '#fff !important' },
        }}>
          <Toolbar sx={{ justifyContent: 'space-between' }}>
            <IconButton edge="start" onClick={handleDrawerToggle}
              sx={{ display: { sm: 'none' } }}>
              <MenuIcon />
            </IconButton>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
              <ThemeSettingsButton />
              <NotificationBell />
              <Typography variant="body2" sx={{ display: { xs: 'none', md: 'block' } }}>
                {user?.firstName} {user?.lastName}
                <Typography component="span" variant="caption" sx={{ ml: 1, color: 'rgba(255,255,255,0.7) !important' }}>
                  ({user?.role?.replace('_', ' ')})
                </Typography>
              </Typography>
              <IconButton size="large" onClick={(e) => setAnchorEl(e.currentTarget)}>
                <AccountCircle />
              </IconButton>
            </Box>

            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              sx={{ '& .MuiPaper-root': { backgroundColor: mode === 'dark' ? '#2a2a2a' : '#fff' } }}>
              <MenuItem disabled><Typography variant="body2">{user?.firstName} {user?.lastName}</Typography></MenuItem>
              <MenuItem disabled><Typography variant="body2" sx={{ color: 'text.secondary' }}>{user?.email}</Typography></MenuItem>
              <MenuItem disabled><Typography variant="body2" sx={{ color: 'text.secondary' }}>{company?.name}</Typography></MenuItem>
              <Divider />
              {isImpersonating && (
                <MenuItem onClick={stopImpersonating} sx={{ color: '#f59e0b' }}>
                  <ListItemIcon><ExitIcon fontSize="small" sx={{ color: '#f59e0b' }} /></ListItemIcon>
                  Exit Impersonation
                </MenuItem>
              )}
              <MenuItem onClick={async () => { await logout(); navigate('/login'); }}>
                <ListItemIcon><Logout fontSize="small" /></ListItemIcon>
                Logout
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        <Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}>
          <Drawer variant="temporary" open={mobileOpen} onClose={handleDrawerToggle}
            ModalProps={{ keepMounted: true }}
            sx={{
              display: { xs: 'block', sm: 'none' },
              '& .MuiDrawer-paper': {
                width: drawerWidth,
                top: isImpersonating ? '36px' : 0,
                backgroundColor: mode === 'dark' ? '#1e1e1e' : '#fff',
              },
            }}>
            {drawer}
          </Drawer>
          <Drawer variant="permanent"
            sx={{
              display: { xs: 'none', sm: 'block' },
              '& .MuiDrawer-paper': {
                width: drawerWidth,
                top: isImpersonating ? '36px' : 0,
                height: isImpersonating ? 'calc(100% - 36px)' : '100%',
                borderRight: '1px solid',
                borderColor: mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
                backgroundColor: mode === 'dark' ? '#1e1e1e' : '#fff',
              },
            }}
            open>
            {drawer}
          </Drawer>
        </Box>

        <Box component="main" sx={{
          flexGrow: 1, p: { xs: 2, sm: 3 },
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          backgroundColor: isMap ? '#1a1a1a !important' : mode === 'dark' ? '#121212 !important' : '#f5f5f5 !important',
          color: isMap ? '#fff !important' : mode === 'dark' ? '#fff !important' : '#000 !important',
          '& .MuiPaper-root': isMap
            ? { backgroundColor: 'rgba(45,45,45,0.9) !important', color: '#fff !important' }
            : mode === 'dark'
              ? { backgroundColor: '#1e1e1e !important', color: '#fff !important' }
              : { backgroundColor: '#fff !important', color: '#000 !important' },
          '& .MuiTableHead-root': {
            backgroundColor: isMap ? '#2a2a2a !important' : mode === 'dark' ? '#2a2a2a !important' : '#f0f4f8 !important',
          },
          '& .MuiTableCell-root': {
            color: isMap ? '#fff !important' : mode === 'dark' ? '#fff !important' : '#000 !important',
          },
        }}>
        <Toolbar />
          <Box sx={{ mx: { xs: -2, sm: -3 }, mt: { xs: -2, sm: -3 }, mb: 2 }}>
            <BillingGraceBanner />
          </Box>
          <BillingLockScreen>
            {children}
          </BillingLockScreen>
        </Box>
      </Box>
    </Box>
  );
};

export default CompanyLayout;